import React, { useEffect, useState } from "react";
import "@/App.css";
import Em2cScene from "@/game/three/Em2cScene";
import TopBar from "@/ui/TopBar";
import BuildSystem from "@/ui/BuildSystem";
import SidePanel from "@/ui/SidePanel";
import ColonyAlertBar from "@/ui/ColonyAlertBar";
import Notifications from "@/ui/Notifications";
import ResearchModal from "@/ui/ResearchModal";
import CargoModal from "@/ui/CargoModal";
import HelpOverlay from "@/ui/HelpOverlay";
import Codex from "@/ui/Codex";
import InfoModal from "@/ui/InfoModal";
import ObjectivesPanel from "@/ui/Objectives";
import HexInfoPanel from "@/ui/HexInfoPanel";
import ResourceDock from "@/ui/ResourceDock";
import DebugPanel from "@/ui/DebugPanel";
import ColonyColorLegend from "@/ui/ColonyColorLegend";
import AwaySummary from "@/ui/AwaySummary";
import GameOver from "@/ui/GameOver";
import TutorialHints from "@/ui/TutorialHints";
import BuildingContextMenu from "@/ui/BuildingContextMenu";
import BuildingDirectory from "@/ui/BuildingDirectory";
import { useGame } from "@/game/em2c-store";
import { startPlayerAutosave } from "@/game/playerSaveFiles";
import "@/game/data/resourceSystemRules";
import "@/game/data/buildingByproductRules";
import { startGameLoop } from "@/game/systems/UnifiedSimulation";
import { installClosedLoopSimulationIntegration } from "@/game/systems/ClosedLoopSimulationIntegration";
import { installMoveBuildingIntegration } from "@/game/systems/MoveBuildingIntegration";
import { installCommandNetworkIntegration } from "@/game/systems/CommandNetworkIntegration";
import { installBuildingUpgradeIntegration } from "@/game/systems/BuildingUpgradeIntegration";
import { installActiveRegistryIntegration } from "@/game/systems/ActiveRegistryIntegration";
import { installEndlessGameIntegration } from "@/game/systems/EndlessGameIntegration";
import {
  installBootstrapBuildIntegration,
  repairBootstrapChainState,
} from "@/game/systems/BootstrapBuildIntegration";
import { installWaterGeologyIntegration } from "@/game/systems/WaterGeologyIntegration";
import { initAudioOnFirstGesture, sfxClick, startAmbientMusic } from "@/game/audio";

const ACTIVE_BUILDING_BOOT_KEY = "mars_active_buildings_boot_v1";
const LEGACY_AUTOSAVE_KEY = "mars_em2c_autosave_v1";
const LEGACY_AUTOSAVE_BACKUP_KEY = "mars_em2c_autosave_pre_active_buildings_v1";
const SYSTEM_AUTOSAVE_KEY = "mars_em2c_systems_autosave_v2";

function App() {
  const [infoOpen, setInfoOpen] = useState(false);
  const cancelPlacement = useGame((s) => s.cancelPlacement);
  const cancelMoveBuilding = useGame((s) => s.cancelMoveBuilding);
  const togglePause = useGame((s) => s.togglePause);
  const setSpeed = useGame((s) => s.setSpeed);
  const rotatePlacement = useGame((s) => s.rotatePlacement);
  const toggleGrid = useGame((s) => s.toggleGrid);
  const placementType = useGame((s) => s.placementType);
  const moveBuildingId = useGame((s) => s.moveBuildingId);

  useEffect(() => {
    try {
      if (!localStorage.getItem(ACTIVE_BUILDING_BOOT_KEY)) {
        const legacyAutosave = localStorage.getItem(LEGACY_AUTOSAVE_KEY);
        if (legacyAutosave && !localStorage.getItem(LEGACY_AUTOSAVE_BACKUP_KEY)) {
          localStorage.setItem(LEGACY_AUTOSAVE_BACKUP_KEY, legacyAutosave);
        }
        localStorage.removeItem(LEGACY_AUTOSAVE_KEY);
        localStorage.removeItem(SYSTEM_AUTOSAVE_KEY);
        localStorage.setItem(ACTIVE_BUILDING_BOOT_KEY, "active-buildings-2026-08-19");
      }
    } catch (e) {
      console.warn("[MarsColony] Active-building save isolation skipped", e);
    }

    installMoveBuildingIntegration();
    installBootstrapBuildIntegration();
    repairBootstrapChainState();
    installWaterGeologyIntegration();
    installActiveRegistryIntegration();
    const stopEndlessMode = installEndlessGameIntegration();
    const stopCommandNetwork = installCommandNetworkIntegration();
    const stopUpgrades = installBuildingUpgradeIntegration();
    const stopGameLoop = startGameLoop();
    const stopPlayerAutosave = startPlayerAutosave(() => useGame.getState());
    const stopClosedLoop = installClosedLoopSimulationIntegration();

    initAudioOnFirstGesture();
    startAmbientMusic();
    return () => {
      if (typeof stopClosedLoop === "function") stopClosedLoop();
      if (typeof stopCommandNetwork === "function") stopCommandNetwork();
      if (typeof stopUpgrades === "function") stopUpgrades();
      if (typeof stopEndlessMode === "function") stopEndlessMode();
      if (typeof stopGameLoop === "function") stopGameLoop();
      stopPlayerAutosave();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (useGame.getState().moveBuildingId) useGame.getState().cancelMoveBuilding?.();
        else cancelPlacement();
      }
      else if (e.key === " ") {
        e.preventDefault();
        togglePause();
        sfxClick();
      } else if (e.key === "1") { setSpeed(1); sfxClick(); }
      else if (e.key === "2") { setSpeed(3); sfxClick(); }
      else if (e.key === "3") { setSpeed(8); sfxClick(); }
      else if (e.key === "r" || e.key === "R") rotatePlacement();
      else if (e.key === "g" || e.key === "G") toggleGrid();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelPlacement, togglePause, setSpeed, rotatePlacement, toggleGrid]);

  const handleWorldPointerDown = (e) => {
    if (e.button === 0) {
      window.dispatchEvent(new CustomEvent("mars:world-click"));
      if (!placementType && !moveBuildingId) useGame.getState().selectBuilding(null);
    }

    if (e.button === 2 && (placementType || moveBuildingId)) {
      e.preventDefault();
      e.stopPropagation();
      if (moveBuildingId) cancelMoveBuilding?.();
      else cancelPlacement();
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden select-none" data-testid="game-root">
      <div
        className="absolute inset-0 z-0"
        onContextMenu={(e) => {
          if (placementType || moveBuildingId) e.preventDefault();
        }}
        onPointerDownCapture={handleWorldPointerDown}
        data-testid="world-canvas-layer"
      >
        <Em2cScene />
      </div>
      <div
        className="absolute inset-0 z-10 pointer-events-none mix-blend-overlay opacity-30"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 3px)",
        }}
      />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.5) 100%)",
        }}
      />
      <TopBar />
      <ColonyColorLegend />
      <BuildingDirectory />
      <ColonyAlertBar />
      <ObjectivesPanel />
      <HexInfoPanel />
      <SidePanel />
      <BuildSystem />
      <ResourceDock />
      <Notifications />
      <BuildingContextMenu />
      <HelpOverlay />
      <Codex />
      <CargoModal />
      <ResearchModal />
      <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
      <AwaySummary />
      <DebugPanel />
      <TutorialHints />
      <GameOver />
    </div>
  );
}

export default App;
